<script lang="ts">
	import CircleHelp from '@lucide/svelte/icons/circle-help'
	import ToggleLeft from '@lucide/svelte/icons/toggle-left'
	import ToggleRight from '@lucide/svelte/icons/toggle-right'
	import { Button as ButtonPrimitive } from 'bits-ui'
	import type { Snippet } from 'svelte'
	import { cn } from 'tailwind-variants'

	import ProgressBars from '#lib/components/progress-bars.svelte'
	import Badge from '#lib/components/ui/badge/badge.svelte'
	import * as Card from '#lib/components/ui/card/index.js'
	import * as Tooltip from '#lib/components/ui/tooltip/index.js'
	import type { ChainId } from '#lib/models/index.js'
	import { updateMetricDialog, type MetricDialogProps } from '#lib/utils/dialogs.svelte.js'
	import { n } from '#lib/utils/navigation.svelte.js'
	import { phg } from '#lib/utils/posthog.js'

	let {
		name,
		leftValue,
		rightValue,
		prettyLeftValue,
		prettyRightValue,
		left,
		right,
		leftPopover,
		leftPopoverType,
		rightPopover,
		rightPopoverType,
		ratio,
		leftRelation,
		rightRelation,
		isNew,
		actionText,
		actionState,
		metric,
		onActionClick,
		children,
	}: {
		name: string
		leftValue?: Nullable<number>
		rightValue?: Nullable<number>
		prettyLeftValue: string
		prettyRightValue: string
		left: { id: ChainId; name: string; slug: string }
		right: { id: ChainId; name: string; slug: string }
		leftPopover?: Nullable<string>
		leftPopoverType?: 'info' | 'warning'
		rightPopover?: Nullable<string>
		rightPopoverType?: 'info' | 'warning'
		ratio?: Nullable<number>
		leftRelation?: Nullable<number>
		rightRelation?: Nullable<number>
		isNew?: boolean
		actionText?: string
		actionState?: boolean
		metric?: MetricDialogProps['metric']
		onActionClick?: Nullable<() => void>
		children?: Snippet
	} = $props()
</script>

<Card.Root
	class={cn('relative', {
		'border-branding-blue': isNew,
		'pointer-events-none cursor-progress opacity-50 select-none': n.l,
	})}
>
	{#if isNew}
		<Badge class="absolute -top-2 -left-2 uppercase" size="3xs" variant="brand">New</Badge>
	{/if}

	<Card.Header>
		<Card.Title tag="h3">
			{name}
		</Card.Title>

		{#if onActionClick || metric}
			<Card.Action class="flex items-center justify-start gap-1.5">
				{#if onActionClick}
					<Tooltip.Root>
						<Tooltip.Trigger
							class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-sm transition-colors focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50"
							aria-label={actionText}
							disabled={n.l}
							onclick={() => onActionClick()}
						>
							{#if actionState}
								<ToggleRight size="21" />
							{:else}
								<ToggleLeft size="21" />
							{/if}
						</Tooltip.Trigger>

						<Tooltip.Content>
							<p>{actionText}</p>
						</Tooltip.Content>
					</Tooltip.Root>
				{/if}

				{#if metric}
					<ButtonPrimitive.Root
						class="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center justify-center rounded-full focus-visible:ring-3"
						aria-label="Show metric explanation"
						onclick={() => {
							phg.capture('metric_dialog_open', { metric })

							updateMetricDialog({ open: true, metric })
						}}
						type="button"
					>
						<CircleHelp size="16" />
					</ButtonPrimitive.Root>
				{/if}
			</Card.Action>
		{/if}
	</Card.Header>

	<Card.Content>
		<p class="text-foreground mb-4">
			{@render children?.()}
		</p>

		<ProgressBars
			{left}
			leftPopover={{
				content: leftPopover,
				ariaLabel: `More info on ${left.name} ${name}`,
				type: leftPopoverType,
			}}
			{leftRelation}
			{leftValue}
			{prettyLeftValue}
			{prettyRightValue}
			{ratio}
			{right}
			rightPopover={{
				content: rightPopover,
				ariaLabel: `More info on ${right.name} ${name}`,
				type: rightPopoverType,
			}}
			{rightRelation}
			{rightValue}
		/>
	</Card.Content>

	<Card.Footer class="mt-auto justify-center">
		<p class="text-center text-xs opacity-80">
			Data from
			<span class="text-branding-gradient font-bold">Chainspect</span>
		</p>
	</Card.Footer>
</Card.Root>
